// Authentication service for standalone webmail client
// Supports: direct login, token from URL (redirect from admin), and session management

// Vite environment variables
declare const import_meta_env: {
  VITE_AUTH_BASE_URL?: string;
  VITE_API_BASE_URL?: string;
  VITE_ADMIN_PORTAL_URL?: string;
};

const getEnv = (key: string, fallback: string): string => {
  try {
    // @ts-ignore - Vite injects import.meta.env at build time
    return (import.meta.env as Record<string, string | undefined>)?.[key] || fallback;
  } catch {
    return fallback;
  }
};

const AUTH_BASE = getEnv('VITE_AUTH_BASE_URL', '') || getEnv('VITE_API_BASE_URL', 'http://localhost:8081');
const ADMIN_PORTAL_URL = getEnv('VITE_ADMIN_PORTAL_URL', 'http://localhost:5173');

const parseErrorMessage = (text: string, fallback: string): string => {
  if (!text) return fallback;
  try {
    const json = JSON.parse(text);
    return json.message || json.error || fallback;
  } catch {
    return text || fallback;
  }
};

// Session storage keys
const TOKEN_KEY = 'webmail_access_token';
const REFRESH_TOKEN_KEY = 'webmail_refresh_token';
const TOKEN_EXPIRY_KEY = 'webmail_token_expiry';

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresIn?: number;
}

export interface LoginCredentials {
  username: string;
  password: string;
  otp?: string;
}

export const authService = {
  /**
   * Get token from URL query parameter (used when redirected from admin portal)
   */
  getTokenFromUrl(): { token: string; refreshToken?: string; expiresIn?: number } | null {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const refreshToken = params.get('refresh_token');
    const expiresInRaw = params.get('expires_in');
    const expiresIn = expiresInRaw ? parseInt(expiresInRaw, 10) : undefined;

    if (token) {
      // Clean URL after extracting token
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      url.searchParams.delete('refresh_token');
      url.searchParams.delete('expires_in');
      window.history.replaceState({}, '', url.toString());

      return {
        token,
        refreshToken: refreshToken || undefined,
        expiresIn: typeof expiresIn === 'number' && Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : undefined
      };
    }
    return null;
  },

  /**
   * Get tokens from session storage
   */
  getStoredTokens(): { accessToken: string; refreshToken: string; expiry: number } | null {
    try {
      const accessToken = sessionStorage.getItem(TOKEN_KEY);
      const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
      const expiry = sessionStorage.getItem(TOKEN_EXPIRY_KEY);

      if (accessToken && refreshToken) {
        return {
          accessToken,
          refreshToken,
          expiry: expiry ? parseInt(expiry, 10) : 0
        };
      }
    } catch (e) {
      console.warn('[AuthService] Failed to read from sessionStorage:', e);
    }
    return null;
  },

  /**
   * Store tokens in session storage
   */
  storeTokens(tokens: AuthTokens): void {
    try {
      sessionStorage.setItem(TOKEN_KEY, tokens.accessToken);
      sessionStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      const expiry = Date.now() + (tokens.expiresIn * 1000);
      sessionStorage.setItem(TOKEN_EXPIRY_KEY, String(expiry));
    } catch (e) {
      console.warn('[AuthService] Failed to write to sessionStorage:', e);
    }
  },

  /**
   * Clear stored tokens
   */
  clearTokens(): void {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(REFRESH_TOKEN_KEY);
      sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
    } catch (e) {
      console.warn('[AuthService] Failed to clear sessionStorage:', e);
    }
  },

  /**
   * Check if stored token is expired or about to expire
   */
  isTokenExpired(bufferSeconds: number = 60): boolean {
    const stored = this.getStoredTokens();
    if (!stored) return true;

    const now = Date.now();
    const expiryWithBuffer = stored.expiry - (bufferSeconds * 1000);
    return now >= expiryWithBuffer;
  },

  /**
   * Login with credentials
   */
  async login(credentials: LoginCredentials): Promise<AuthTokens> {
    const res = await fetch(`${AUTH_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials)
    });

    const text = await res.text();

    if (!res.ok) {
      throw new Error(parseErrorMessage(text, `Login failed: ${res.status}`));
    }

    const data = JSON.parse(text);
    const tokens: AuthTokens = {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresIn: data.expiresIn || 3600,
      refreshExpiresIn: data.refreshExpiresIn
    };

    this.storeTokens(tokens);
    return tokens;
  },

  /**
   * Request a password reset token
   */
  async requestPasswordReset(email: string): Promise<{ resetToken: string; note?: string }> {
    const res = await fetch(`${AUTH_BASE}/api/public/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    const text = await res.text();
    if (!res.ok) {
      throw new Error(parseErrorMessage(text, `Reset request failed: ${res.status}`));
    }

    return JSON.parse(text) as { resetToken: string; note?: string };
  },

  /**
   * Reset password using token + OTP
   */
  async resetPassword(payload: { email: string; resetToken: string; otp: string; newPassword: string }): Promise<void> {
    const res = await fetch(`${AUTH_BASE}/api/public/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const text = await res.text();
    if (!res.ok) {
      throw new Error(parseErrorMessage(text, `Reset failed: ${res.status}`));
    }
  },

  /**
   * Update password for the current user
   */
  async updatePassword(newPassword: string): Promise<void> {
    const stored = this.getStoredTokens();
    if (!stored?.accessToken) {
      throw new Error('Not authenticated');
    }

    const res = await fetch(`${AUTH_BASE}/api/me/password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${stored.accessToken}`
      },
      body: JSON.stringify({ password: newPassword })
    });

    const text = await res.text();
    if (!res.ok) {
      throw new Error(parseErrorMessage(text, `Password update failed: ${res.status}`));
    }
  },

  /**
   * Refresh the access token using refresh token
   */
  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    const res = await fetch(`${AUTH_BASE}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken })
    });

    const text = await res.text();

    if (!res.ok) {
      this.clearTokens();
      throw new Error('Session expired. Please login again.');
    }

    const data = JSON.parse(text);
    const tokens: AuthTokens = {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken || refreshToken,
      expiresIn: data.expiresIn || 3600,
      refreshExpiresIn: data.refreshExpiresIn
    };

    this.storeTokens(tokens);
    return tokens;
  },

  /**
   * Logout - revoke session on server and clear local tokens
   * Retries on failure to ensure session is properly revoked
   */
  async logout(): Promise<void> {
    const stored = this.getStoredTokens();

    // Try to revoke refresh token on server with retry
    if (stored?.refreshToken) {
      const maxRetries = 3;
      let lastError: Error | null = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const response = await fetch(`${AUTH_BASE}/api/auth/logout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: stored.refreshToken })
          });

          if (response.ok) {
            console.log('[AuthService] Session revoked successfully');
            break;
          }

          // If server returns an error, don't retry (token might already be invalid)
          if (response.status === 401 || response.status === 400) {
            console.log('[AuthService] Session already invalid or revoked');
            break;
          }

          lastError = new Error(`Logout failed with status ${response.status}`);
        } catch (error) {
          lastError = error instanceof Error ? error : new Error('Network error during logout');
          console.warn(`[AuthService] Logout attempt ${attempt}/${maxRetries} failed:`, error);

          // Wait before retry (exponential backoff)
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 500));
          }
        }
      }

      if (lastError) {
        console.error('[AuthService] Failed to revoke session after retries:', lastError);
        // Continue to clear local tokens even if server revocation failed
      }
    }

    this.clearTokens();
  },

  /**
   * Redirect to admin portal login page
   */
  redirectToAdminLogin(): void {
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `${ADMIN_PORTAL_URL}?return_url=${returnUrl}`;
  },

  /**
   * Get admin portal URL for "Back to Admin" link
   */
  getAdminPortalUrl(): string {
    return ADMIN_PORTAL_URL;
  },

  /**
   * Initialize authentication - check URL params, then stored tokens
   */
  async initializeAuth(): Promise<{ accessToken: string; refreshToken: string } | null> {
    // 1. Check for token in URL (redirected from admin portal)
    const urlTokens = this.getTokenFromUrl();
    if (urlTokens) {
      // Store the tokens from URL
      const tokens: AuthTokens = {
        accessToken: urlTokens.token,
        refreshToken: urlTokens.refreshToken || '',
        expiresIn: urlTokens.expiresIn || 3600
      };

      if (urlTokens.refreshToken) {
        this.storeTokens(tokens);
      }

      return {
        accessToken: urlTokens.token,
        refreshToken: urlTokens.refreshToken || ''
      };
    }

    // 2. Check stored tokens
    const stored = this.getStoredTokens();
    if (stored) {
      // If token is expired or about to expire, try to refresh
      if (this.isTokenExpired(60)) {
        try {
          const refreshed = await this.refreshToken(stored.refreshToken);
          return {
            accessToken: refreshed.accessToken,
            refreshToken: refreshed.refreshToken
          };
        } catch {
          // Refresh failed, tokens are invalid
          this.clearTokens();
          return null;
        }
      }

      return {
        accessToken: stored.accessToken,
        refreshToken: stored.refreshToken
      };
    }

    return null;
  }
};
